<template>
    <div class="app-container">
      <el-row :gutter="20">
        <!-- 左侧导航与筛选区 -->
        <el-col :span="5">
          <el-card class="left-panel" shadow="never">
            <div class="department-header">
              <h3>科室管理</h3>
              <el-button type="text" icon="el-icon-refresh" @click="refreshData">刷新</el-button>
            </div>
            
            <!-- 搜索框 -->
            <div class="search-box">
              <el-input
                placeholder="搜索患者/职工姓名"
                v-model="searchKeyword"
                prefix-icon="el-icon-search"
                clearable
              ></el-input>
            </div>
            
            <!-- 科室树形控件 -->
            <div class="department-tree">
              <el-tree
                :data="departmentTree"
                :props="defaultProps"
                accordion
                @node-click="handleNodeClick"
                node-key="id"
                :default-expanded-keys="['1']"
                highlight-current
              ></el-tree>
            </div>
            
            <!-- 留言记录查询 -->
            <div class="message-search-section">
              <h3>留言记录查询</h3>
              <el-form :model="queryForm" size="small" label-width="80px">
                <el-form-item label="开始日期">
                  <el-date-picker
                    v-model="queryForm.startDate"
                    type="date"
                    placeholder="选择开始日期"
                    value-format="yyyy-MM-dd"
                    style="width: 100%;"
                  ></el-date-picker>
                </el-form-item>
                <el-form-item label="结束日期">
                  <el-date-picker
                    v-model="queryForm.endDate"
                    type="date"
                    placeholder="选择结束日期"
                    value-format="yyyy-MM-dd"
                    style="width: 100%;"
                  ></el-date-picker>
                </el-form-item>
                <el-form-item label="关键词">
                  <el-input v-model="queryForm.keyword" placeholder="内容关键词"></el-input>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" icon="el-icon-search" @click="searchMessages">查询</el-button>
                  <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
                </el-form-item>
              </el-form>
            </div>
          </el-card>
        </el-col>
        
        <!-- 中间主内容区 -->
        <el-col :span="13">
          <el-card shadow="never">
            <div slot="header" class="clearfix">
              <span>全院患者留言与回复记录总览</span>
              <div class="header-operations">
                <el-radio-group v-model="filterType" size="small" @change="handleFilterChange">
                  <el-radio-button label="all">全部</el-radio-button>
                  <el-radio-button label="patient">患者</el-radio-button>
                  <el-radio-button label="doctor">医生</el-radio-button>
                  <el-radio-button label="manager">个管师</el-radio-button>
                  <el-radio-button label="project">项目管理师</el-radio-button>
                </el-radio-group>
                <el-button type="primary" size="small" icon="el-icon-download" @click="exportChatRecords">
                  导出记录
                </el-button>
                <el-dropdown @command="handleBatchCommand" trigger="click">
                  <el-button type="text">
                    批量操作<i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="markRead">标记已读</el-dropdown-item>
                    <el-dropdown-item command="export">批量导出</el-dropdown-item>
                    <el-dropdown-item command="assign">分配处理</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </div>
            
            <!-- 高级筛选区域 -->
            <div class="filter-container">
              <el-tag
                v-for="tag in activeTags"
                :key="tag.value"
                closable
                @close="removeFilterTag(tag)"
                effect="plain"
                type="info"
              >
                {{ tag.label }}: {{ tag.text }}
              </el-tag>
              
              <el-button v-if="!showFilter" class="filter-button" type="text" @click="toggleFilter">
                高级筛选 <i class="el-icon-arrow-down"></i>
              </el-button>
              <el-button v-else class="filter-button" type="text" @click="toggleFilter">
                收起筛选 <i class="el-icon-arrow-up"></i>
              </el-button>
            </div>
            
            <!-- 高级筛选面板 -->
            <div v-show="showFilter" class="advanced-filter">
              <el-form :inline="true" :model="advancedFilter" size="small">
                <el-form-item label="科室">
                  <el-select v-model="advancedFilter.department" placeholder="选择科室">
                    <el-option
                      v-for="dept in departments"
                      :key="dept.value"
                      :label="dept.label"
                      :value="dept.value">
                    </el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="处理状态">
                  <el-select v-model="advancedFilter.status" placeholder="处理状态">
                    <el-option label="全部" value=""></el-option>
                    <el-option label="已处理" value="processed"></el-option>
                    <el-option label="待处理" value="pending"></el-option>
                    <el-option label="已转发" value="forwarded"></el-option>
                    <el-option label="异常" value="abnormal"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="紧急程度">
                  <el-select v-model="advancedFilter.priority" placeholder="紧急程度">
                    <el-option label="全部" value=""></el-option>
                    <el-option label="高" value="high"></el-option>
                    <el-option label="中" value="medium"></el-option>
                    <el-option label="低" value="low"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="applyAdvancedFilter">应用筛选</el-button>
                  <el-button @click="resetAdvancedFilter">重置</el-button>
                </el-form-item>
              </el-form>
            </div>
            
            <!-- 留言记录列表 -->
            <div class="message-list">
              <el-table
                :data="messageRecords"
                style="width: 100%"
                @selection-change="handleSelectionChange"
                v-loading="loading"
              >
                <el-table-column type="selection" width="55"></el-table-column>
                
                <el-table-column type="expand">
                  <template slot-scope="props">
                    <el-card class="message-detail" shadow="never">
                      <div class="conversation-thread">
                        <div v-for="(msg, index) in props.row.conversation" 
                             :key="index" 
                             :class="['message-item', msg.fromPatient ? 'patient-message' : 'staff-message']">
                          <div class="message-header">
                            <span class="sender">{{ msg.sender }} <span class="role">({{ msg.role }})</span></span>
                            <span class="timestamp">{{ formatDateTime(msg.timestamp) }}</span>
                          </div>
                          <div class="message-content" v-html="msg.content"></div>
                          
                          <!-- 附件显示 -->
                          <div v-if="msg.attachments && msg.attachments.length > 0" class="attachments">
                            <div v-for="(attachment, idx) in msg.attachments" :key="idx" class="attachment-item">
                              <i class="el-icon-document"></i>
                              <span>{{ attachment.name }} ({{ attachment.size }})</span>
                            </div>
                          </div>
                          
                          <!-- 图片显示 -->
                          <div v-if="msg.images && msg.images.length > 0" class="images">
                            <el-image
                              v-for="(img, imgIdx) in msg.images"
                              :key="imgIdx"
                              :src="img"
                              :preview-src-list="msg.images"
                              fit="cover"
                              class="message-image"
                            ></el-image>
                          </div>
                          
                          <!-- 转发信息 -->
                          <div v-if="msg.forwardInfo" class="forward-info">
                            <el-alert
                              :title="msg.forwardInfo.title"
                              :type="msg.forwardInfo.type"
                              :description="msg.forwardInfo.desc"
                              :closable="false"
                              show-icon
                              size="small"
                            ></el-alert>
                          </div>
                        </div>
                      </div>
                      
                      <!-- 管理操作区 -->
                      <div class="admin-operations">
                        <el-button type="text" @click="viewAuditLog(props.row)">查看处理日志</el-button>
                        <el-button type="text" @click="reassignHandler(props.row)">重新分配</el-button>
                        <el-button type="text" @click="markMessageReviewed(props.row)">标记已审核</el-button>
                      </div>
                    </el-card>
                  </template>
                </el-table-column>
                
                <el-table-column prop="patientName" label="患者姓名" width="120">
                  <template slot-scope="scope">
                    <el-link type="primary" @click="viewPatientDetail(scope.row)">{{ scope.row.patientName }}</el-link>
                    <el-tag v-if="scope.row.urgent" size="mini" type="danger">紧急</el-tag>
                  </template>
                </el-table-column>
                
                <el-table-column prop="department" label="科室" width="120"></el-table-column>
                
                <el-table-column prop="latestMessage" label="最新留言" min-width="250">
                  <template slot-scope="scope">
                    <div class="message-preview">
                      <span class="preview-sender">[{{ scope.row.latestSender }}]</span>
                      {{ scope.row.latestMessage }}
                    </div>
                    <div class="message-tags">
                      <el-tag v-if="scope.row.hasAttachment" size="mini" type="info">附件</el-tag>
                      <el-tag v-if="scope.row.hasImage" size="mini" type="info">图片</el-tag>
                    </div>
                  </template>
                </el-table-column>
                
                <el-table-column prop="responder" label="处理人" width="120">
                  <template slot-scope="scope">
                    <span v-if="scope.row.responder">{{ scope.row.responder }}</span>
                    <el-tag v-else size="mini" type="warning">未处理</el-tag>
                  </template>
                </el-table-column>
                
                <el-table-column prop="responseTime" label="处理时间" width="100">
                  <template slot-scope="scope">
                    <span>{{ formatDate(scope.row.responseTime) || '未处理' }}</span>
                  </template>
                </el-table-column>
                
                <el-table-column prop="responseSpeed" label="响应速度" width="100">
                  <template slot-scope="scope">
                    <el-tag :type="getResponseSpeedType(scope.row.responseSpeed)" size="mini">
                      {{ getResponseSpeedText(scope.row.responseSpeed) }}
                    </el-tag>
                  </template>
                </el-table-column>
                
                <el-table-column prop="status" label="状态" width="100">
                  <template slot-scope="scope">
                    <el-tag :type="getStatusType(scope.row.status)">{{ scope.row.status }}</el-tag>
                  </template>
                </el-table-column>
                
                <el-table-column label="操作" width="150" fixed="right">
                  <template slot-scope="scope">
                    <el-button
                      size="mini"
                      type="text"
                      @click="viewMessageDetail(scope.row)"
                    >查看</el-button>
                    <el-button
                      size="mini"
                      type="text"
                      @click="urgeHandler(scope.row)"
                    >催办</el-button>
                    <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, scope.row)">
                      <span class="el-dropdown-link">
                        更多<i class="el-icon-arrow-down el-icon--right"></i>
                      </span>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="exportSingle">导出</el-dropdown-item>
                        <el-dropdown-item command="reassign">重新分配</el-dropdown-item>
                        <el-dropdown-item command="feedback">满意度查看</el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <!-- 分页 -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.currentPage"
                :page-sizes="[10, 20, 30, 50]"
                :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
              ></el-pagination>
            </div>
          </el-card>
        </el-col>
        
        <!-- 右侧统计与管理工具区 -->
        <el-col :span="6">
          <el-card class="statistic-card" shadow="never">
            <div slot="header">
              <span>留言处理情况统计</span>
              <el-dropdown style="float: right" @command="handleStatPeriod">
                <span class="el-dropdown-link">
                  {{ statPeriodText }}<i class="el-icon-arrow-down el-icon--right"></i>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="day">今日</el-dropdown-item>
                  <el-dropdown-item command="week">本周</el-dropdown-item>
                  <el-dropdown-item command="month">本月</el-dropdown-item>
                  <el-dropdown-item command="quarter">本季度</el-dropdown-item>
                  <el-dropdown-item command="year">本年度</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            
            <!-- 统计数据卡片 -->
            <div class="stat-summary">
              <el-row :gutter="20">
                <el-col :span="8">
                  <div class="stat-card">
                    <div class="stat-value">{{ stats.totalMessages }}</div>
                    <div class="stat-label">总留言</div>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="stat-card">
                    <div class="stat-value">{{ stats.responseRate }}%</div>
                    <div class="stat-label">回复率</div>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="stat-card">
                    <div class="stat-value">{{ stats.avgResponseTime }}</div>
                    <div class="stat-label">平均响应(分钟)</div>
                  </div>
                </el-col>
              </el-row>
            </div>
            
            <!-- 回复率统计图表 -->
            <div>
              <h4 class="chart-title">回复率统计</h4>
              <div id="responseRateChart" class="chart-container"></div>
            </div>
            
            <!-- 平均响应时长 -->
            <div>
              <h4 class="chart-title">平均响应时长(分钟)</h4>
              <div id="responseTimeChart" class="chart-container"></div>
            </div>
            
            <!-- 患者满意度 -->
            <div>
              <h4 class="chart-title">患者满意度</h4>
              <div id="satisfactionChart" class="chart-container"></div>
            </div>
            
            <!-- 留言异常预警 -->
            <div class="warning-section">
              <div class="section-header">
                <h4>留言异常预警</h4>
                <el-button type="text" @click="handleAllWarnings">查看全部</el-button>
              </div>
              
              <el-collapse v-model="activeWarnings">
                <el-collapse-item v-for="(warning, index) in warnings" :key="index" :name="index">
                  <template slot="title">
                    <div class="warning-header">
                      <el-badge :type="getWarningBadgeType(warning.level)" is-dot></el-badge>
                      <span class="warning-title">{{ warning.title }}</span>
                      <span class="warning-time">{{ formatDate(warning.time) }}</span>
                    </div>
                  </template>
                  
                  <div class="warning-content">
                    <p>{{ warning.description }}</p>
                    <div class="warning-actions">
                      <el-button size="mini" type="primary" @click.stop="handleWarning(warning)">处理</el-button>
                      <el-button size="mini" type="text" @click.stop="ignoreWarning(warning, index)">忽略</el-button>
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
              
              <div v-if="warnings.length === 0" class="no-warnings">
                <i class="el-icon-circle-check"></i>
                <p>暂无异常预警</p>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
  
      <!-- 患者详情对话框 -->
      <el-dialog title="患者详情" :visible.sync="patientDetailVisible" width="800px">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="姓名">{{ currentPatient.name }}</el-descriptions-item>
          <el-descriptions-item label="性别">{{ currentPatient.gender === 'male' ? '男' : '女' }}</el-descriptions-item>
          <el-descriptions-item label="年龄">{{ currentPatient.age }}岁</el-descriptions-item>
          <el-descriptions-item label="病历号">{{ currentPatient.recordNum }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ currentPatient.phone }}</el-descriptions-item>
          <el-descriptions-item label="诊断">{{ currentPatient.diagnosis }}</el-descriptions-item>
          <el-descriptions-item label="主治医师">{{ currentPatient.doctor }}</el-descriptions-item>
          <el-descriptions-item label="随访项目">{{ currentPatient.project || '暂无' }}</el-descriptions-item>
          <el-descriptions-item label="随访阶段">{{ currentPatient.phase || '暂无' }}</el-descriptions-item>
          <el-descriptions-item label="下次随访">{{ currentPatient.nextFollowUp || '暂无' }}</el-descriptions-item>
        </el-descriptions>
        
        <el-tabs v-model="patientDetailActiveTab" type="card">
          <el-tab-pane label="随访记录" name="records">
            <el-timeline>
              <el-timeline-item
                v-for="(record, index) in patientFollowUpRecords"
                :key="index"
                :timestamp="formatDateTime(record.date)"
                :type="record.type === '异常随访' ? 'danger' : ''"
              >
                <div class="follow-up-record">
                  <h4>{{ record.type }} - {{ record.doctor }}</h4>
                  <p>{{ record.summary }}</p>
                  <el-button size="mini" type="text" @click="viewFollowUpDetail(record)">查看详情</el-button>
                </div>
              </el-timeline-item>
            </el-timeline>
          </el-tab-pane>
          <el-tab-pane label="检查报告" name="reports">
            <el-table :data="patientReports" style="width: 100%">
              <el-table-column prop="date" label="日期" width="180"></el-table-column>
              <el-table-column prop="type" label="检查类型" width="180"></el-table-column>
              <el-table-column prop="result" label="结果"></el-table-column>
              <el-table-column label="操作" width="120">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="viewReport(scope.row)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane label="满意度反馈" name="feedback">
            <el-rate
              v-model="patientSatisfaction"
              disabled
              show-text
              :colors="['#F56C6C', '#E6A23C', '#67C23A']"
              :texts="['不满意', '一般', '满意', '很满意', '非常满意']"
            >
            </el-rate>
            <div v-if="patientFeedback" class="feedback-content">
              <h4>反馈内容：</h4>
              <p>{{ patientFeedback }}</p>
            </div>
            <div v-else class="no-feedback">
              <i class="el-icon-warning-outline"></i>
              <p>该患者暂无满意度反馈</p>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-dialog>
      
      <!-- 重新分配对话框 -->
      <el-dialog title="重新分配处理人员" :visible.sync="reassignDialogVisible" width="500px">
        <el-form :model="reassignForm" label-width="100px">
          <el-form-item label="当前处理人">
            <span>{{ reassignForm.currentHandler || '无' }}</span>
          </el-form-item>
          <el-form-item label="选择新处理人" required>
            <el-select v-model="reassignForm.newHandler" placeholder="请选择处理人员" style="width: 100%">
              <el-option-group
                v-for="group in staffOptions"
                :key="group.label"
                :label="group.label"
              >
                <el-option
                  v-for="item in group.options"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-option-group>
            </el-select>
          </el-form-item>
          <el-form-item label="转交原因" required>
            <el-input type="textarea" v-model="reassignForm.reason" :rows="3" placeholder="请填写转交原因"></el-input>
          </el-form-item>
          <el-form-item label="通知方式">
            <el-checkbox-group v-model="reassignForm.notifyMethod">
              <el-checkbox label="系统通知">系统通知</el-checkbox>
              <el-checkbox label="短信">短信</el-checkbox>
              <el-checkbox label="微信">微信</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="紧急程度">
            <el-radio-group v-model="reassignForm.priority">
              <el-radio label="high">紧急</el-radio>
              <el-radio label="medium">普通</el-radio>
              <el-radio label="low">低优先级</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="reassignDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="confirmReassign">确 定</el-button>
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import * as echarts from 'echarts';
  
  export default {
    name: "HospitalAdmin",
    data() {
      return {
        // 科室树
        departmentTree: [
          {
            id: '1',
            label: '全院科室',
            children: [
              { id: '2', label: '内科', children: [
                { id: '21', label: '心内科' },
                { id: '22', label: '消化内科' },
                { id: '23', label: '内分泌科' }
              ]},
              { id: '3', label: '外科', children: [
                { id: '31', label: '普外科' },
                { id: '32', label: '骨科' },
                { id: '33', label: '神经外科' }
              ]},
              { id: '4', label: '妇产科' },
              { id: '5', label: '儿科' },
              { id: '6', label: '专科', children: [
                { id: '61', label: '眼科' },
                { id: '62', label: '耳鼻喉科' },
                { id: '63', label: '皮肤科' }
              ]},
              { id: '7', label: '医技科室', children: [
                { id: '71', label: '放射科' },
                { id: '72', label: '检验科' },
                { id: '73', label: '病理科' }
              ]},
              { id: '8', label: '随访中心' }
            ]
          }
        ],
        defaultProps: {
          children: 'children',
          label: 'label'
        },
        
        // 搜索关键词
        searchKeyword: '',
        
        // 查询表单
        queryForm: {
          startDate: '',
          endDate: '',
          keyword: ''
        },
        
        // 过滤相关
        filterType: 'all',
        showFilter: false,
        advancedFilter: {
          department: '',
          status: '',
          priority: ''
        },
        activeTags: [],
        
        // 科室选项
        departments: [
          { value: '21', label: '心内科' },
          { value: '22', label: '消化内科' },
          { value: '23', label: '内分泌科' },
          { value: '31', label: '普外科' },
          { value: '32', label: '骨科' },
          { value: '33', label: '神经外科' },
          { value: '4', label: '妇产科' },
          { value: '5', label: '儿科' },
          { value: '61', label: '眼科' },
          { value: '62', label: '耳鼻喉科' },
          { value: '63', label: '皮肤科' },
          { value: '8', label: '随访中心' }
        ],
        
        // 数据加载状态
        loading: false,
        
        // 分页
        pagination: {
          currentPage: 1,
          pageSize: 10,
          total: 0
        },
        
        // 选中的记录
        selectedRecords: [],
        
        // 留言记录数据
        messageRecords: [],
        
        // 统计数据
        statPeriod: 'week',
        statPeriodText: '本周',
        stats: {
          totalMessages: 0,
          responseRate: 0,
          avgResponseTime: 0
        },
        
        // 图表实例
        responseRateChart: null,
        responseTimeChart: null,
        satisfactionChart: null,
        
        // 异常预警
        warnings: [],
        activeWarnings: [0],
        
        // 对话框控制
        patientDetailVisible: false,
        patientDetailActiveTab: 'records',
        reassignDialogVisible: false,
        
        // 当前患者数据
        currentPatient: {},
        patientFollowUpRecords: [],
        patientReports: [],
        patientSatisfaction: 0,
        patientFeedback: '',
        
        // 重新分配表单
        reassignForm: {
          currentHandler: '',
          newHandler: '',
          reason: '',
          notifyMethod: ['系统通知'],
          priority: 'medium'
        },
        
        // 员工选项
        staffOptions: [
          {
            label: '医生',
            options: [
              { value: 'd1', label: '张医生（心内科主治医师）' },
              { value: 'd2', label: '王医生（内分泌科副主任医师）' },
              { value: 'd3', label: '李医生（肿瘤科主任医师）' },
              { value: 'd4', label: '赵医生（神经内科主治医师）' },
            ]
          },
          {
            label: '个案管理师',
            options: [
              { value: 'm1', label: '刘管理（随访中心）' },
              { value: 'm2', label: '陈管理（心内科）' },
              { value: 'm3', label: '孙管理（肿瘤科）' },
            ]
          },
          {
            label: '项目管理师',
            options: [
              { value: 'p1', label: '赵主任（随访中心）' },
              { value: 'p2', label: '钱主任（心内科）' },
            ]
          }
        ]
      };
    },
    
    created() {
      // 初始化数据
      this.loadMessageRecords();
      this.loadStatistics();
      this.loadWarnings();
    },
    
    mounted() {
      // 初始化图表
      this.$nextTick(() => {
        this.initCharts();
      });
    },
    
    beforeDestroy() {
      // 销毁图表实例
      if (this.responseRateChart) {
        this.responseRateChart.dispose();
      }
      if (this.responseTimeChart) {
        this.responseTimeChart.dispose();
      }
      if (this.satisfactionChart) {
        this.satisfactionChart.dispose();
      }
    },
    
    methods: {
      // 加载留言记录
      loadMessageRecords() {
        this.loading = true;
        
        // 模拟异步加载
        setTimeout(() => {
          this.messageRecords = [
            {
              id: 1,
              patientName: '许昊然',
              department: '心内科',
              latestMessage: '医生，我最近血压不太稳定，有时候会头晕是怎么回事？',
              latestSender: '许昊然',
              hasAttachment: false,
              hasImage: false,
              responder: '王医生',
              responseTime: new Date(2024, 5, 18, 14, 30),
              responseSpeed: 25, // 分钟
              status: '已处理',
              urgent: false,
              conversation: [
                {
                  fromPatient: true,
                  sender: '许昊然',
                  role: '患者',
                  content: '医生，我最近血压不太稳定，有时候会头晕是怎么回事？',
                  timestamp: new Date(2024, 5, 18, 14, 5)
                },
                {
                  fromPatient: false,
                  sender: '王医生',
                  role: '主治医师',
                  content: '您好，您的症状可能与血压波动有关。请问您测量的血压值是多少？头晕是在什么时候发生的？请详细描述一下您的情况。',
                  timestamp: new Date(2024, 5, 18, 14, 30)
                }
              ]
            },
            {
              id: 2,
              patientName: '苏雅婷',
              department: '内分泌科',
              latestMessage: '这是我上周的血糖监测记录，请医生查看',
              latestSender: '苏雅婷',
              hasAttachment: true,
              hasImage: false,
              responder: '刘管理',
              responseTime: new Date(2024, 5, 17, 16, 45),
              responseSpeed: 120,
              status: '已处理',
              urgent: false,
              conversation: [
                {
                  fromPatient: true,
                  sender: '苏雅婷',
                  role: '患者',
                  content: '这是我上周的血糖监测记录，请医生查看',
                  timestamp: new Date(2024, 5, 17, 14, 45),
                  attachments: [
                    {
                      name: '血糖监测记录.xlsx',
                      size: '15KB'
                    }
                  ]
                },
                {
                  fromPatient: false,
                  sender: '刘管理',
                  role: '个案管理师',
                  content: '李先生您好，我已收到您的血糖监测记录。您的血糖控制情况还需改善，我已将记录转给王医生，医生稍后会联系您调整用药方案。',
                  timestamp: new Date(2024, 5, 17, 16, 45),
                  forwardInfo: {
                    title: '已转发王医生处理',
                    type: 'info',
                    desc: '已将患者血糖监测记录转交内分泌科王医生评估'
                  }
                }
              ]
            },
            {
              id: 3,
              patientName: '林书辰',
              department: '肿瘤科',
              latestMessage: '这是我最近伤口的照片，感觉有些红肿',
              latestSender: '林书辰',
              hasAttachment: false,
              hasImage: true,
              responder: '李医生',
              responseTime: new Date(2024, 5, 17, 10, 20),
              responseSpeed: 35,
              status: '已处理',
              urgent: true,
              conversation: [
                {
                  fromPatient: true,
                  sender: '林书辰',
                  role: '患者',
                  content: '这是我最近伤口的照片，感觉有些红肿',
                  timestamp: new Date(2024, 5, 17, 9, 45),
                  images: ['https://shadow.elemecdn.com/a/17/d43b41abc8c413096227590fb94aejpeg.jpeg']
                },
                {
                  fromPatient: false,
                  sender: '李医生',
                  role: '主任医师',
                  content: '王先生您好，从照片来看，伤口确实有轻微感染迹象。建议您今天下午来门诊处理，我已为您预约了14:00的门诊，请务必准时。',
                  timestamp: new Date(2024, 5, 17, 10, 20)
                }
              ]
            },
            {
              id: 4,
              patientName: '程若曦',
              department: '内分泌科',
              latestMessage: '请问我的甲状腺功能检查结果出来了吗？',
              latestSender: '程若曦',
              hasAttachment: false,
              hasImage: false,
              responder: '',
              responseTime: null,
              responseSpeed: 0,
              status: '待处理',
              urgent: false,
              conversation: [
                {
                  fromPatient: true,
                  sender: '程若曦',
                  role: '患者',
                  content: '请问我的甲状腺功能检查结果出来了吗？',
                  timestamp: new Date(2024, 5, 18, 16, 30)
                }
              ]
            },
            {
              id: 5,
              patientName: '郭子轩',
              department: '心内科',
              latestMessage: '谢谢赵主任和整个医疗团队的悉心照顾，我现在感觉比以前好多了',
              latestSender: '郭子轩',
              hasAttachment: false,
              hasImage: false,
              responder: '赵主任',
              responseTime: new Date(2024, 5, 15, 14, 0),
              responseSpeed: 45,
              status: '已结束',
              urgent: false,
              conversation: [
                {
                  fromPatient: false,
                  sender: '赵主任',
                  role: '项目管理师',
                  content: '钱先生，您好！恭喜您成功完成了为期4个月的随访项目。根据最终评估结果，您的治疗效果非常理想，各项指标均恢复正常。感谢您的积极配合！如后续有任何问题，您仍可以联系我们。',
                  timestamp: new Date(2024, 5, 15, 14, 0)
                },
                {
                  fromPatient: true,
                  sender: '郭子轩',
                  role: '患者',
                  content: '谢谢赵主任和整个医疗团队的悉心照顾，我现在感觉比以前好多了。我想问一下，虽然项目结束了，我还需要定期去医院复查吗？',
                  timestamp: new Date(2024, 5, 18, 9, 30)
                }
              ]
            }
          ];
          
          // 更新分页数据
          this.pagination.total = 45; // 模拟总记录数
          this.loading = false;
        }, 600);
      },
      
      // 加载统计数据
      loadStatistics() {
        // 模拟统计数据
        this.stats = {
          totalMessages: 324,
          responseRate: 96.5,
          avgResponseTime: 42
        };
      },
      
      // 加载异常预警
      loadWarnings() {
        // 模拟异常预警数据
        this.warnings = [
          {
            id: 1,
            level: 'critical',
            title: '回复严重延迟',
            description: '患者林书辰(肿瘤科)的留言已超过24小时未回复，患者多次催问，请尽快处理。',
            time: new Date(2024, 5, 18, 8, 30)
          },
          {
            id: 2,
            level: 'warning',
            title: '患者投诉预警',
            description: '患者苏雅婷(内分泌科)对最近的随访服务表示不满，多次提到医生回复不及时，建议关注。',
            time: new Date(2024, 5, 17, 16, 15)
          },
          {
            id: 3,
            level: 'info',
            title: '新增高频关注问题',
            description: '近期多位患者咨询关于"新药副作用"的问题，建议相关科室准备统一解答口径。',
            time: new Date(2024, 5, 16, 14, 45)
          }
        ];
      },
      
      // 初始化图表
      initCharts() {
        // 回复率图表
        this.responseRateChart = echarts.init(document.getElementById('responseRateChart'));
        const responseRateOption = {
          tooltip: {
            trigger: 'item'
          },
          legend: {
            orient: 'horizontal',
            bottom: 0
          },
          series: [
            {
              name: '留言处理情况',
              type: 'pie',
              radius: ['50%', '70%'],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 5,
                borderColor: '#fff',
                borderWidth: 1
              },
              label: {
                show: false,
                position: 'center'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: 16,
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              data: [
                { value: 96.5, name: '已回复', itemStyle: { color: '#67C23A' } },
                { value: 3.5, name: '未回复', itemStyle: { color: '#F56C6C' } }
              ]
            }
          ]
        };
        this.responseRateChart.setOption(responseRateOption);
        
        // 响应时长图表
        this.responseTimeChart = echarts.init(document.getElementById('responseTimeChart'));
        const responseTimeOption = {
          tooltip: {
            trigger: 'axis'
          },
          xAxis: {
            type: 'category',
            data: ['心内科', '消化科', '内分泌', '肿瘤科', '妇产科', '儿科', '随访中心']
          },
          yAxis: {
            type: 'value',
            name: '分钟'
          },
          series: [
            {
              data: [35, 48, 42, 52, 28, 32, 25],
              type: 'bar',
              itemStyle: {
                color: '#409EFF'
              }
            }
          ]
        };
        this.responseTimeChart.setOption(responseTimeOption);
        
        // 满意度图表
        this.satisfactionChart = echarts.init(document.getElementById('satisfactionChart'));
        const satisfactionOption = {
          tooltip: {
            trigger: 'axis'
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              type: 'line',
              smooth: true,
              lineStyle: {
                width: 3,
                color: '#E6A23C'
              },
              symbolSize: 7,
              data: [4.6, 4.5, 4.7, 4.8, 4.6, 4.9, 4.7]
            }
          ]
        };
        this.satisfactionChart.setOption(satisfactionOption);
        
        // 窗口大小变化时重绘图表
        window.addEventListener('resize', () => {
          this.responseRateChart.resize();
          this.responseTimeChart.resize();
          this.satisfactionChart.resize();
        });
      },
      
      // 树节点点击
      handleNodeClick(data) {
        if (data.children) {
          // 如果是父节点，不执行筛选
          return;
        }
        
        // 添加科室筛选
        this.addFilterTag({
          key: 'department',
          value: data.id,
          label: '科室',
          text: data.label
        });
        
        // 重新加载数据
        this.loadMessageRecords();
      },
      
      // 搜索留言记录
      searchMessages() {
        // 添加时间筛选标签
        if (this.queryForm.startDate && this.queryForm.endDate) {
          this.addFilterTag({
            key: 'dateRange',
            value: [this.queryForm.startDate, this.queryForm.endDate],
            label: '时间范围',
            text: `${this.queryForm.startDate} 至 ${this.queryForm.endDate}`
          });
        }
        
        // 添加关键词筛选标签
        if (this.queryForm.keyword) {
          this.addFilterTag({
            key: 'keyword',
            value: this.queryForm.keyword,
            label: '关键词',
            text: this.queryForm.keyword
          });
        }
        
        // 重新加载数据
        this.loadMessageRecords();
      },
      
      // 重置查询条件
      resetQuery() {
        this.queryForm = {
          startDate: '',
          endDate: '',
          keyword: ''
        };
      },
      
      // 切换高级筛选
      toggleFilter() {
        this.showFilter = !this.showFilter;
      },
      
      // 应用高级筛选
      applyAdvancedFilter() {
        // 添加部门筛选标签
        if (this.advancedFilter.department) {
          const dept = this.departments.find(d => d.value === this.advancedFilter.department);
          if (dept) {
            this.addFilterTag({
              key: 'department',
              value: this.advancedFilter.department,
              label: '科室',
              text: dept.label
            });
          }
        }
        
        // 添加状态筛选标签
        if (this.advancedFilter.status) {
          const statusMap = {
            'processed': '已处理',
            'pending': '待处理',
            'forwarded': '已转发',
            'abnormal': '异常'
          };
          
          this.addFilterTag({
            key: 'status',
            value: this.advancedFilter.status,
            label: '状态',
            text: statusMap[this.advancedFilter.status]
          });
        }
        
        // 添加紧急程度筛选标签
        if (this.advancedFilter.priority) {
          const priorityMap = {
            'high': '紧急',
            'medium': '普通',
            'low': '低优先级'
          };
          
          this.addFilterTag({
            key: 'priority',
            value: this.advancedFilter.priority,
            label: '紧急程度',
            text: priorityMap[this.advancedFilter.priority]
          });
        }
        
        // 重新加载数据
        this.loadMessageRecords();
        
        // 收起筛选面板
        this.showFilter = false;
      },
      
      // 重置高级筛选
      resetAdvancedFilter() {
        this.advancedFilter = {
          department: '',
          status: '',
          priority: ''
        };
      },
      
      // 添加筛选标签
      addFilterTag(tag) {
        // 移除同类型的标签
        this.activeTags = this.activeTags.filter(t => t.key !== tag.key);
        
        // 添加新标签
        this.activeTags.push(tag);
      },
      
      // 移除筛选标签
      removeFilterTag(tag) {
        this.activeTags = this.activeTags.filter(t => t.key !== tag.key);
        
        // 重新加载数据
        this.loadMessageRecords();
      },
      
      // 处理筛选类型变化
      handleFilterChange(value) {
        const filterTypeMap = {
          'all': '全部',
          'patient': '患者',
          'doctor': '医生',
          'manager': '个管师',
          'project': '项目管理师'
        };
        
        if (value !== 'all') {
          this.addFilterTag({
            key: 'filterType',
            value,
            label: '角色',
            text: filterTypeMap[value]
          });
        } else {
          // 移除角色筛选标签
          this.activeTags = this.activeTags.filter(t => t.key !== 'filterType');
        }
        
        // 重新加载数据
        this.loadMessageRecords();
      },
      
      // 表格选择变化
      handleSelectionChange(selection) {
        this.selectedRecords = selection;
      },
      
      // 导出聊天记录
      exportChatRecords() {
        this.$message.success('正在导出留言记录，请稍候...');
        setTimeout(() => {
          this.$notify({
            title: '导出成功',
            message: '留言记录已成功导出为Excel文件',
            type: 'success'
          });
        }, 1500);
      },
      
      // 处理批量操作
      handleBatchCommand(command) {
        if (this.selectedRecords.length === 0) {
          this.$message.warning('请先选择记录');
          return;
        }
        
        switch (command) {
          case 'markRead':
            this.$message.success(`已标记${this.selectedRecords.length}条记录为已读`);
            break;
          case 'export':
            this.$message.success(`正在导出${this.selectedRecords.length}条记录，请稍候...`);
            break;
          case 'assign':
            this.reassignDialogVisible = true;
            this.reassignForm.currentHandler = '';
            break;
        }
      },
      
      // 处理下拉菜单命令
      handleCommand(command, row) {
        switch (command) {
          case 'exportSingle':
            this.$message.success(`正在导出患者${row.patientName}的留言记录，请稍候...`);
            break;
          case 'reassign':
            this.reassignDialogVisible = true;
            this.reassignForm.currentHandler = row.responder;
            this.currentRecord = row;
            break;
          case 'feedback':
            this.viewPatientDetail(row);
            this.patientDetailActiveTab = 'feedback';
            break;
        }
      },
      
      // 催办处理人
      urgeHandler(row) {
        if (!row.responder) {
          this.$message.warning('该留言尚未分配处理人');
          return;
        }
        
        this.$confirm(`确定要催办${row.responder}尽快处理${row.patientName}的留言?`, '催办确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.$message.success(`已成功向${row.responder}发送催办通知`);
        }).catch(() => {});
      },
      
      // 查看留言详情
      viewMessageDetail(row) {
        // 此功能已通过表格的expand行实现
      },
      
      // 查看处理日志
      viewAuditLog(row) {
        this.$message.info(`查看患者${row.patientName}的留言处理日志`);
      },
      
      // 重新分配处理人
      reassignHandler(row) {
        this.reassignDialogVisible = true;
        this.reassignForm.currentHandler = row.responder;
        this.currentRecord = row;
      },
      
      // 确认重新分配
      confirmReassign() {
        if (!this.reassignForm.newHandler) {
          this.$message.warning('请选择新的处理人员');
          return;
        }
        
        if (!this.reassignForm.reason) {
          this.$message.warning('请填写转交原因');
          return;
        }
        
        // 获取处理人姓名
        const handler = this.staffOptions.flatMap(group => group.options)
          .find(staff => staff.value === this.reassignForm.newHandler);
        
        const handlerName = handler ? handler.label.split('（')[0] : '未知处理人';
        
        this.$message.success(`已将留言转交给${handlerName}处理`);
        this.reassignDialogVisible = false;
      },
      
      // 标记留言已审核
      markMessageReviewed(row) {
        this.$message.success(`已将患者${row.patientName}的留言标记为已审核`);
      },
      
      // 刷新数据
      refreshData() {
        this.loading = true;
        
        setTimeout(() => {
          this.loadMessageRecords();
          this.loadStatistics();
          this.loadWarnings();
          
          // 重新初始化图表
          this.initCharts();
          
          this.$message.success('数据已刷新');
        }, 600);
      },
      
      // 查看患者详情
      viewPatientDetail(row) {
        // 模拟加载患者详情
        this.currentPatient = {
          name: row.patientName,
          gender: Math.random() > 0.5 ? 'male' : 'female',
          age: Math.floor(Math.random() * 50) + 20,
          recordNum: `HF${Math.floor(Math.random() * 100000)}`,
          phone: `138${Math.floor(Math.random() * 100000000).toString().padStart(8, '0')}`,
          diagnosis: row.department === '心内科' ? '冠心病，高血压' : (row.department === '内分泌科' ? '2型糖尿病' : '术后恢复'),
          doctor: row.responder || '未分配',
          project: Math.random() > 0.3 ? '慢病管理项目' : '术后随访项目',
          phase: Math.random() > 0.5 ? '随访中' : '治疗期',
          nextFollowUp: new Date(Date.now() + Math.random() * 30 * 24 * 3600 * 1000).toLocaleDateString()
        };
        
        // 随访记录
        this.patientFollowUpRecords = Array(Math.floor(Math.random() * 4) + 1).fill().map((_, i) => ({
          date: new Date(Date.now() - (i * 15 + Math.random() * 5) * 24 * 3600 * 1000),
          type: i === 0 && Math.random() > 0.7 ? '异常随访' : '常规随访',
          doctor: row.responder || '主治医生',
          summary: i === 0 && Math.random() > 0.7 ? 
            '患者血压/血糖波动较大，需调整治疗方案' : 
            '患者状况稳定，继续当前治疗方案'
        }));
        
        // 检查报告
        this.patientReports = Array(Math.floor(Math.random() * 5) + 2).fill().map((_, i) => ({
          date: new Date(Date.now() - (i * 20 + Math.random() * 10) * 24 * 3600 * 1000).toLocaleDateString(),
          type: ['血常规', '尿常规', '肝功能', 'B超', 'CT', '心电图'][Math.floor(Math.random() * 6)],
          result: Math.random() > 0.8 ? '异常' : '正常'
        }));
        
        // 患者满意度
        this.patientSatisfaction = Math.floor(Math.random() * 2) + 3;
        this.patientFeedback = Math.random() > 0.3 ? 
          '医生和管理师回复很及时，解答也很专业，感谢医院提供的随访服务！' : 
          '';
        
        this.patientDetailVisible = true;
      },
      
      // 查看随访详情
      viewFollowUpDetail(record) {
        this.$message.info(`查看${this.currentPatient.name}在${this.formatDate(record.date)}的${record.type}详情`);
      },
      
      // 查看检查报告
      viewReport(report) {
        this.$message.info(`查看${this.currentPatient.name}的${report.type}检查报告`);
      },
      
      // 查看全部预警
      handleAllWarnings() {
        this.$router.push('/follow_up/warnings');
      },
      
      // 处理预警
      handleWarning(warning) {
        this.$prompt('请输入处理意见', '预警处理', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputType: 'textarea'
        }).then(({ value }) => {
          this.$message({
            type: 'success',
            message: '预警已标记为处理完成'
          });
          
          // 从列表中移除
          this.warnings = this.warnings.filter(w => w.id !== warning.id);
        }).catch(() => {});
      },
      
      // 忽略预警
      ignoreWarning(warning, index) {
        this.$confirm('确认忽略此预警？忽略后将不再提示。', '确认信息', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          // 从列表中移除
          this.warnings.splice(index, 1);
          this.activeWarnings = this.activeWarnings.filter(i => i !== index);
          
          this.$message({
            type: 'success',
            message: '预警已忽略'
          });
        }).catch(() => {});
      },
      
      // 处理统计周期选择
      handleStatPeriod(command) {
        this.statPeriod = command;
        
        const periodMap = {
          day: '今日',
          week: '本周',
          month: '本月',
          quarter: '本季度',
          year: '本年度'
        };
        
        this.statPeriodText = periodMap[command];
        
        // 重新加载统计数据
        this.loadStatistics();
        
        // 重新初始化图表
        this.initCharts();
      },
      
      // 分页相关方法
      handleSizeChange(val) {
          this.pagination.pageSize = val;
        this.loadMessageRecords();
      },
      
      handleCurrentChange(val) {
        this.pagination.currentPage = val;
        this.loadMessageRecords();
      },
      
      // 格式化日期
      formatDate(date) {
        if (!date) return '';
        if (typeof date === 'string') return date;
        
        const d = new Date(date);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      },
      
      // 格式化日期时间
      formatDateTime(date) {
        if (!date) return '';
        if (typeof date === 'string') return date;
        
        const d = new Date(date);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const hour = d.getHours().toString().padStart(2, '0');
        const minute = d.getMinutes().toString().padStart(2, '0');
        
        return `${year}-${month}-${day} ${hour}:${minute}`;
      },
      
      // 格式化时间
      formatTime(date) {
        if (!date) return '';
        
        const d = new Date(date);
        const hour = d.getHours().toString().padStart(2, '0');
        const minute = d.getMinutes().toString().padStart(2, '0');
        
        return `${hour}:${minute}`;
      },
      
      // 获取响应速度类型
      getResponseSpeedType(speed) {
        if (!speed) return '';
        
        if (speed <= 30) return 'success'; // 30分钟内
        if (speed <= 120) return 'warning'; // 2小时内
        return 'danger'; // 超过2小时
      },
      
      // 获取响应速度文本
      getResponseSpeedText(speed) {
        if (!speed) return '未响应';
        
        if (speed <= 30) return '快速';
        if (speed <= 120) return '一般';
        return '较慢';
      },
      
      // 获取状态类型
      getStatusType(status) {
        switch (status) {
          case '已处理':
            return 'success';
          case '待处理':
            return 'warning';
          case '已转发':
            return 'info';
          case '已结束':
            return '';
          case '异常':
            return 'danger';
          default:
            return '';
        }
      },
      
      // 获取预警徽章类型
      getWarningBadgeType(level) {
        switch (level) {
          case 'critical':
            return 'danger';
          case 'warning':
            return 'warning';
          case 'info':
            return 'info';
          default:
            return '';
        }
      }
    }
  };
  </script>
  
  <style scoped>
  /* 布局相关样式 */
  .app-container {
    padding: 10px;
  }
  
  .left-panel {
    height: calc(100vh - 140px);
    overflow-y: auto;
  }
  
  /* 头部样式 */
  .department-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  
  .department-header h3 {
    margin: 0;
  }
  
  /* 搜索框样式 */
  .search-box {
    margin-bottom: 15px;
  }
  
  /* 科室树样式 */
  .department-tree {
    margin-bottom: 20px;
    max-height: 300px;
    overflow-y: auto;
  }
  
  /* 留言查询区域 */
  .message-search-section {
    margin-top: 20px;
    border-top: 1px solid #ebeef5;
    padding-top: 15px;
  }
  
  .message-search-section h3 {
    margin-bottom: 15px;
  }
  
  /* 表头操作区 */
  .header-operations {
    float: right;
    display: flex;
    align-items: center;
  }
  
  .header-operations .el-radio-group {
    margin-right: 15px;
  }
  
  /* 筛选标签 */
  .filter-container {
    margin-bottom: 15px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
  
  .filter-container .el-tag {
    margin-right: 8px;
    margin-bottom: 8px;
  }
  
  .filter-button {
    margin-left: auto;
  }
  
  /* 高级筛选面板 */
  .advanced-filter {
    background-color: #f9f9f9;
    padding: 15px;
    margin-bottom: 15px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
  }
  
  /* 分页容器 */
  .pagination-container {
    margin-top: 15px;
    display: flex;
    justify-content: center;
  }
  
  /* 统计卡片样式 */
  .statistic-card {
    height: calc(100vh - 140px);
    overflow-y: auto;
  }
  
  .stat-summary {
    margin-bottom: 20px;
  }
  
  .stat-card {
    background-color: #f5f7fa;
    border-radius: 4px;
    padding: 10px;
    text-align: center;
    height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .stat-value {
    font-size: 24px;
    font-weight: bold;
    color: #409EFF;
  }
  
  .stat-label {
    font-size: 12px;
    margin-top: 5px;
    color: #606266;
  }
  
  /* 图表容器 */
  .chart-title {
    margin-top: 20px;
    margin-bottom: 10px;
    font-size: 14px;
    color: #303133;
  }
  
  .chart-container {
    height: 200px;
    margin-bottom: 15px;
  }
  
  /* 预警部分 */
  .warning-section {
    margin-top: 20px;
  }
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  
  .section-header h4 {
    margin: 0;
  }
  
  .warning-header {
    display: flex;
    align-items: center;
  }
  
  .warning-title {
    margin-left: 10px;
    flex: 1;
  }
  
  .warning-time {
    color: #909399;
    font-size: 12px;
  }
  
  .warning-content {
    padding: 10px 15px;
  }
  
  .warning-actions {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
  }
  
  .no-warnings {
    text-align: center;
    color: #67C23A;
    padding: 20px 0;
  }
  
  .no-warnings i {
    font-size: 24px;
    margin-bottom: 10px;
  }
  
  /* 消息记录样式 */
  .message-detail {
    padding: 0;
  }
  
  .conversation-thread {
    padding: 15px;
  }
  
  .message-item {
    padding: 10px 15px;
    margin-bottom: 10px;
    border-radius: 4px;
  }
  
  .patient-message {
    background-color: #f2f6fc;
    border-left: 3px solid #409EFF;
  }
  
  .staff-message {
    background-color: #f5f7fa;
    border-left: 3px solid #67C23A;
  }
  
  .message-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  
  .sender {
    font-weight: bold;
  }
  
  .role {
    font-weight: normal;
    color: #606266;
  }
  
  .timestamp {
    color: #909399;
    font-size: 12px;
  }
  
  .message-content {
    line-height: 1.6;
    word-break: break-word;
  }
  
  .attachments, .images {
    margin-top: 8px;
  }
  
  .attachment-item {
    display: flex;
    align-items: center;
    background-color: #f9f9f9;
    padding: 5px 10px;
    border-radius: 4px;
    margin-bottom: 5px;
  }
  
  .attachment-item i {
    margin-right: 8px;
  }
  
  .message-image {
    width: 100px;
    height: 100px;
    margin-right: 8px;
    margin-bottom: 8px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .forward-info {
    margin-top: 10px;
  }
  
  .admin-operations {
    padding: 10px 15px;
    background-color: #f5f7fa;
    border-top: 1px solid #ebeef5;
    text-align: right;
  }
  
  /* 留言预览 */
  .message-preview {
    font-size: 13px;
    color: #606266;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;
  }
  
  .preview-sender {
    color: #409EFF;
    margin-right: 5px;
  }
  
  .message-tags {
    margin-top: 5px;
  }
  
  /* 患者详情 */
  .follow-up-record {
    padding: 5px 0;
  }
  
  .feedback-content {
    margin-top: 15px;
  }
  
  .no-feedback {
    text-align: center;
    color: #909399;
    padding: 20px 0;
  }
  
  .no-feedback i {
    font-size: 24px;
    margin-bottom: 10px;
  }
  
  /* 响应式调整 */
  @media screen and (max-width: 1400px) {
    .el-col-5 {
      width: 25%;
    }
    
    .el-col-13 {
      width: 50%;
    }
    
    .el-col-6 {
      width: 25%;
    }
  }
  
  @media screen and (max-width: 992px) {
    .el-col-5 {
      width: 100%;
      margin-bottom: 20px;
    }
    
    .el-col-13 {
      width: 65%;
    }
    
    .el-col-6 {
      width: 35%;
    }
    
    .left-panel {
      height: auto;
      max-height: 500px;
    }
  }
  </style>